import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { organizationsAPI, adminAPI } from '@/services';
import PageHeader from '../components/PageHeader';

const SaasDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [orgStats, setOrgStats] = useState({
    totalOrganizations: 0,
    activeOrganizations: 0,
    inactiveOrganizations: 0,
    recentOrganizations: []
  });
  const [globalStats, setGlobalStats] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadSubscriptionRevenue = async (organizations = []) => {
    const ids = (organizations || [])
      .map((o) => String(o?.id || '').trim())
      .filter(Boolean);
    if (ids.length === 0) return 0;

    const responses = await Promise.all(
      ids.map((id) =>
        adminAPI
          .getOrganizationBillingTransactions(id, { source_type: 'subscription', limit: 1 })
          .then((r) => r.data)
          .catch(() => null)
      )
    );

    return responses.reduce((sum, payload) => {
      const rows = payload?.summary?.by_source_type || [];
      const subRow = rows.find((r) => String(r?._id || '').toLowerCase() === 'subscription');
      return sum + Number(subRow?.total_amount || 0);
    }, 0);
  };

  useEffect(() => {
    loadStats();
  }, [activeTab]);

  const loadStats = async () => {
    try {
      setLoading(true);
      
      // Charger les stats des organisations
      const orgResponse = await organizationsAPI.getAll();
      const orgs = orgResponse.data || [];
      
      const active = orgs.filter(o => o.is_active).length;
      const inactive = orgs.length - active;
      const recent = orgs
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      setOrgStats({
        totalOrganizations: orgs.length,
        activeOrganizations: active,
        inactiveOrganizations: inactive,
        recentOrganizations: recent
      });

      // Charger les stats globales
      if (activeTab === 'overview' || activeTab === 'stats') {
        try {
          const statsResponse = await adminAPI.getStats();
          const subscriptionRevenue = await loadSubscriptionRevenue(orgs);
          const merged = {
            ...(statsResponse.data || {}),
            financial: {
              ...((statsResponse.data || {}).financial || {}),
              subscription_revenue: subscriptionRevenue,
            },
          };
          setGlobalStats(merged);
        } catch (err) {
          console.error('Erreur lors du chargement des stats globales:', err);
        }
      }

      // Charger les utilisateurs actifs
      if (activeTab === 'users') {
        try {
          const usersResponse = await adminAPI.getActiveUsers();
          setActiveUsers(usersResponse.data.users || []);
        } catch (err) {
          console.error('Erreur lors du chargement des utilisateurs actifs:', err);
        }
      }
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !orgStats.totalOrganizations && !globalStats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Uruganda Orgs - Dashboard SaaS"
        description="Plateforme multi-organisations - Vue d'ensemble du système"
      />

      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Organisations</p>
              <p className="text-4xl font-bold mt-2">{orgStats.totalOrganizations}</p>
            </div>
            <div className="p-4 bg-white/20 rounded-xl">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Organisations Actives</p>
              <p className="text-4xl font-bold mt-2">{orgStats.activeOrganizations}</p>
            </div>
            <div className="p-4 bg-white/20 rounded-xl">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Organisations Inactives</p>
              <p className="text-4xl font-bold mt-2">{orgStats.inactiveOrganizations}</p>
            </div>
            <div className="p-4 bg-white/20 rounded-xl">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'overview'
                  ? 'border-b-2 border-entrea-royal text-entrea-royal'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Vue d'ensemble
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'stats'
                  ? 'border-b-2 border-entrea-royal text-entrea-royal'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Statistiques globales
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'users'
                  ? 'border-b-2 border-entrea-royal text-entrea-royal'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Utilisateurs actifs
            </button>
            <button
              onClick={() => navigate('/saas/admin/audit')}
              className="px-6 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300"
            >
              Logs d'audit
            </button>
            <button
              onClick={() => navigate('/saas/admin/system')}
              className="px-6 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300"
            >
              Système
            </button>
          </nav>
        </div>
      </div>

      {/* Vue d'ensemble */}
      {activeTab === 'overview' && (
        <>
          {/* Stats globales si disponibles */}
          {globalStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Utilisateurs</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{globalStats.users?.total || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">{globalStats.users?.active || 0} actifs</p>
                  </div>
                  <div className="p-3 bg-entrea-panel rounded-lg">
                    <svg className="w-6 h-6 text-entrea-ink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Membres</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{globalStats.members?.total || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">{globalStats.members?.active || 0} actifs</p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Revenus abonnements</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      {globalStats.financial?.subscription_revenue ? 
                        new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(globalStats.financial.subscription_revenue)
                        : '0 FCFA'
                      }
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Logs d'audit</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{globalStats.audit?.total_logs || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">{globalStats.audit?.logs_last_30_days || 0} (30j)</p>
                  </div>
                  <div className="p-3 bg-entrea-panel rounded-lg">
                    <svg className="w-6 h-6 text-entrea-royal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions rapides */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => navigate('/saas/organizations')}
                className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-entrea-sky/50 transition group"
              >
                <div className="p-2 bg-entrea-panel rounded-lg group-hover:bg-entrea-sky/35 transition">
                  <svg className="w-6 h-6 text-entrea-royal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">Organisations</p>
                  <p className="text-sm text-gray-500">Gérer toutes les organisations</p>
                </div>
              </button>

              <button
                onClick={() => navigate('/register-organization')}
                className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-green-300 transition group"
              >
                <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">Créer Organisation</p>
                  <p className="text-sm text-gray-500">Nouvelle organisation</p>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('users')}
                className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-purple-300 transition group"
              >
                <div className="p-2 bg-entrea-panel rounded-lg group-hover:bg-purple-200 transition">
                  <svg className="w-6 h-6 text-entrea-ink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">Utilisateurs</p>
                  <p className="text-sm text-gray-500">Tous les utilisateurs</p>
                </div>
              </button>

              <button
                onClick={() => navigate('/saas/admin/audit')}
                className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition group"
              >
                <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">Audit Logs</p>
                  <p className="text-sm text-gray-500">Historique système</p>
                </div>
              </button>
            </div>
          </div>

          {/* Organisations récentes */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Organisations récentes</h3>
              <button
                onClick={() => navigate('/saas/organizations')}
                className="text-sm text-entrea-royal hover:text-entrea-ink font-medium"
              >
                Voir toutes →
              </button>
            </div>
            {orgStats.recentOrganizations.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Aucune organisation</p>
            ) : (
              <div className="space-y-3">
                {orgStats.recentOrganizations.map((org) => (
                  <div
                    key={org.id}
                    onClick={() => navigate(`/saas/organizations/${org.id}`)}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-entrea-sky/50 transition cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-gradient-to-br from-entrea-ink to-entrea-royal rounded-lg flex items-center justify-center text-white font-bold">
                        {org.name?.charAt(0).toUpperCase() || 'O'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{org.name}</p>
                        <p className="text-sm text-gray-500 font-mono">{org.subdomain}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        org.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {org.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className="text-sm text-gray-500">
                        {org.created_at ? new Date(org.created_at).toLocaleDateString('fr-FR') : '-'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Statistiques globales détaillées */}
      {activeTab === 'stats' && globalStats && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Utilisateurs</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{globalStats.users?.total || 0}</p>
                  <p className="text-sm text-gray-500 mt-1">{globalStats.users?.active || 0} actifs</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Membres</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{globalStats.members?.total || 0}</p>
                  <p className="text-sm text-gray-500 mt-1">{globalStats.members?.active || 0} actifs</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Revenus abonnements</p>
                  <p className="text-2xl font-bold text-green-600 mt-2">
                    {globalStats.financial?.subscription_revenue ? 
                      new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF', maximumFractionDigits: 0 }).format(globalStats.financial.subscription_revenue)
                      : '0 FCFA'
                    }
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Dépenses</p>
                  <p className="text-2xl font-bold text-red-600 mt-2">
                    {globalStats.financial?.total_expenses ? 
                      new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF', maximumFractionDigits: 0 }).format(globalStats.financial.total_expenses)
                      : '0 FCFA'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Répartition par rôle */}
          {globalStats.users?.by_role && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition des utilisateurs par rôle</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(globalStats.users.by_role).map(([role, count]) => (
                  <div key={role} className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 capitalize">{role.replace('_', ' ')}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{count}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Utilisateurs actifs */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Utilisateurs actifs</h3>
          {activeUsers.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucun utilisateur actif</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilisateur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rôle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernière activité</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activeUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{user.name || user.username}</div>
                        <div className="text-sm text-gray-500">{user.email || user.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 capitalize">
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.last_activity ? new Date(user.last_activity).toLocaleString('fr-FR') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          user.is_active_session
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.is_active_session ? 'Session active' : 'Inactif'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SaasDashboard;

